/**
 * Друкована форма «Акт передачі сировини в переробку» (довільна форма,
 * ч.677): організація-замовник, № і дата, переробник + договір +
 * очікувана дата повернення, таблиця сировини зі складом ПО РЯДКАХ
 * (0461; колонка — лише при >1 ефективному складі), собівартість для
 * проведеного; підписи. Єдиний стиль друку (канон ч.669).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { materialTransfersForProcessing } from '@/api/materialTransfersForProcessing';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { contractLabel } from '@/shared/utils/labels';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string | null | undefined) =>
  Number(n ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MaterialTransferPrintPage() {
  const { id } = useParams<{ id: string }>();
  // ч.678 (рішення власника): вартість (наша собівартість) за замовчуванням
  // НЕ друкується — переробнику її бачити не треба. Галка — для
  // внутрішнього примірника (матвідповідальність).
  const [showCost, setShowCost] = useState(false);

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['material-transfer-for-processing', id],
    queryFn: () => materialTransfersForProcessing.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const contractsQ = useQuery({
    queryKey: ['contracts', d?.organization_id, d?.processor_contractor_id],
    queryFn: () => refs.contracts({
      organization_id: d!.organization_id,
      contractor_id: d!.processor_contractor_id,
    }),
    enabled: !!d?.organization_id && !!d?.processor_contractor_id,
  });
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження акта…</div>;
  }

  const whName = new Map((whQ.data ?? []).map((w) => [w.id, w.name]));
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const processor = cQ.data?.items.find((c) => c.id === d.processor_contractor_id);
  const contract = (contractsQ.data ?? []).find((c) => c.id === d.contract_id);
  // ч.677: склад рядка > склад шапки (legacy). Колонка «Склад» — лише
  // коли ефективних складів більше одного (канон друку ч.661/669).
  const effWh = (it: { warehouse_id?: string | null }) =>
    it.warehouse_id || d.source_warehouse_id || '';
  const multiWarehouse = new Set(d.items.map(effWh).filter(Boolean)).size > 1;
  const singleWhId = d.items.map(effWh).find(Boolean) ?? '';
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const totalCost = d.items.reduce((s, it) => s + Number(it.total_cost ?? 0), 0);
  // ч.689: договірна оцінка для переробника (його 022) — друкується завжди,
  // коли заповнена (на відміну від нашої собівартості під галкою).
  const hasDeclared = d.items.some((it) => it.declared_value != null && Number(it.declared_value) > 0);
  const totalDeclared = d.items.reduce((s, it) => s + Number(it.declared_value ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .mt-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .mt-table th, .mt-table td { border: 1px solid #000; padding: 1px 4px; }
        .mt-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.material_transfer_for_processing',
        documentId: d.id,
        filename: `Передача в переробку ${d.document_number}`,
        orgName: org.name,
      }} />

      {d.posted && (
        <label className="print-hide" style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          fontSize: '0.85rem', marginBottom: '0.75rem', cursor: 'pointer',
        }}>
          <input type="checkbox" checked={showCost}
            onChange={(e) => setShowCost(e.target.checked)}
            style={{ width: 'auto' }} />
          показати вартість (собівартість — для внутрішнього примірника,
          переробнику не показуємо)
        </label>
      )}

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт передачі сировини в переробку № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10pt', marginBottom: '0.5rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '55%' }}>
              <span style={{ color: '#555' }}>Переробник: </span>
              <strong>{processor?.name ?? '—'}</strong>
              {partyCodesText(processor) && <span style={{ color: '#555' }}> ({partyCodesText(processor)})</span>}
            </td>
            <td>
              <span style={{ color: '#555' }}>Договір: </span>
              {contract ? contractLabel(contract) : 'без договору'}
            </td>
          </tr>
          <tr>
            <td>
              <span style={{ color: '#555' }}>Склад відвантаження: </span>
              {multiWarehouse
                ? 'за рядками (див. таблицю)'
                : (whName.get(singleWhId) ?? '—')}
            </td>
            <td>
              <span style={{ color: '#555' }}>Очікуване повернення: </span>
              {d.expected_return_date
                ? new Date(d.expected_return_date).toLocaleDateString('uk-UA')
                : '—'}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="mt-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Сировина (найменування)</th>
            {multiWarehouse && <th style={{ width: 130 }}>Склад</th>}
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 80 }}>К-сть</th>
            {hasDeclared && <th style={{ width: 100 }}>Задекл. вартість</th>}
            {d.posted && showCost && <th style={{ width: 80 }}>Ціна</th>}
            {d.posted && showCost && <th style={{ width: 90 }}>Сума</th>}
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}{nom?.code && <span style={{ color: '#555' }}> ({nom.code})</span>}</td>
                {multiWarehouse && <td>{whName.get(effWh(it)) ?? '—'}</td>}
                <td style={{ textAlign: 'center' }}>{uomOf(it)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(it.quantity)}</td>
                {hasDeclared && (
                  <td style={{ textAlign: 'right' }}>
                    {it.declared_value != null ? fmtMoney(it.declared_value) : '—'}
                  </td>
                )}
                {d.posted && showCost && <td style={{ textAlign: 'right' }}>{fmtMoney(it.cost_per_unit)}</td>}
                {d.posted && showCost && <td style={{ textAlign: 'right' }}>{fmtMoney(it.total_cost)}</td>}
              </tr>
            );
          })}
        </tbody>
        {(hasDeclared || (d.posted && showCost)) && (
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={multiWarehouse ? 5 : 4} style={{ textAlign: 'right' }}>Разом:</td>
              {hasDeclared && (
                <td style={{ textAlign: 'right' }}>{fmtMoney(totalDeclared)}</td>
              )}
              {d.posted && showCost && <td></td>}
              {d.posted && showCost && (
                <td style={{ textAlign: 'right' }}>{fmtMoney(d.total_cost ?? totalCost)}</td>
              )}
            </tr>
          </tfoot>
        )}
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Всього найменувань {d.items.length}
        {hasDeclared && <>, задекларована вартість{' '}
          <strong>{fmtMoney(totalDeclared)} грн</strong></>}
        {d.posted && showCost && <>, собівартість <strong>{fmtMoney(d.total_cost ?? totalCost)} грн</strong></>}.
        {' '}Сировина передається на давальницьких умовах і залишається
        власністю замовника{hasDeclared && <>; переробник несе матеріальну
        відповідальність у межах задекларованої вартості</>}.
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Передав (МВО складу)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Прийняв (представник переробника)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б., довіреність</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
